import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Database } from "lucide-react";
import PublicPageLayout from "@/components/PublicPageLayout";
import Seo, { pageSchema } from "@/components/Seo";
import { WhatsAppButton } from "@/components/WhatsAppFab";
import { Button } from "@/components/ui/button";
import { WA_MESSAGES, PRICING, COVERAGE_FACTS } from "@/lib/site";

const DATA_FIELDS = [
  "GSTIN", "Legal Name of Business", "Trade Name", "Constitution of Business",
  "Registration Date", "GST Status", "Taxpayer Type", "State Jurisdiction",
  "Centre Jurisdiction", "Principal Place of Business", "City", "PIN Code",
  "Nature of Business Activity", "Principal Business Address",
];

const USE_CASES = [
  { t: "B2B Wholesalers & Distributors", d: "Onboard new GST-registered businesses as B2B buyers of your products." },
  { t: "SaaS & FinTech", d: "Sell GST-return software, accounting, invoicing and reconciliation tools to newly registered taxpayers." },
  { t: "Banks & Payment Companies", d: "Offer current accounts, POS terminals and MSME loans to fresh GST registrations." },
  { t: "Logistics & Supply Chain", d: "Pitch B2B shipping, warehousing and transport services to new commercial entities." },
  { t: "Consultants & CAs", d: "Provide GST return filing, reconciliation, audit and advisory to newly registered businesses." },
  { t: "Marketing Agencies", d: "Reach new businesses with branding, digital marketing and lead-gen services." },
];

export default function GstDatabase() {
  const path = "/gst-database";
  const title = "Buy GST Company Database India | New GST Registrations Daily | Registry Feed";
  const description = "Fresh GST registered company database — newly registered businesses with GSTIN, verified contacts. Pan-India coverage. Trial ₹3,000. Daily updates.";
  const keywords = "buy gst company list, gst database provider, new gst registration list, fresh gst registration data india, gst data provider india, gst leads for banks india";
  const bc = [{ name: "Home", path: "/" }, { name: "GST Company Database", path }];
  const jsonLd = [
    ...pageSchema({ title, description, path, breadcrumbs: bc }),
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "GST Company Database",
      description: "Daily-updated database of newly registered GST taxpayers across all Indian states and union territories.",
      brand: { "@type": "Brand", name: "Registry Feed" },
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        price: String(PRICING.gst.price),
        availability: "https://schema.org/InStock",
        url: "https://registryfeed.com/gst-database",
      },
    },
  ];
  return (
    <PublicPageLayout breadcrumbs={bc}>
      <Seo title={title} description={description} keywords={keywords} path={path} jsonLd={jsonLd} />

      <section className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14 sm:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" /> GST Company Database
          </div>
          <h1 className="mt-6 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            GST Company Database — New GST Registrations, Updated Daily
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Registry Feed delivers a fresh GST business database every day. Reach the freshest GST-registered proprietorships, partnerships, private limited companies and LLPs — filtered by state or city — with clean, importable CSV files.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <WhatsAppButton message={WA_MESSAGES.gst} label="Get GST Data on WhatsApp" testid="gst-cta-whatsapp" />
            <Link to="/sample-data"><Button size="lg" variant="outline">Request Sample</Button></Link>
            <Link to="/pricing"><Button size="lg" variant="ghost">Get Pricing <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      {/* What is GST Registration Database — keyword-rich explainer */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">What is GST Registration Database?</h2>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-3xl">
            A GST registration database is a list of businesses that have newly obtained a GSTIN — proprietorships, partnerships, LLPs and companies of every size. As a dedicated GST database provider, Registry Feed delivers a fresh new GST registration list every day, covering all 36 states and union territories. Each record includes business name, GSTIN, owner name, state, city, registration date and business category, delivered as Excel/CSV in your dashboard. Sales teams choose us as their GST data provider India-wide because fresh GST registration data India reaches businesses at the exact moment they start trading and need banking, credit, insurance, accounting and marketing services. Our GST leads for banks India are widely used for current account, loan and POS campaigns. Buy GST company list access with a 3-day trial at ₹3,000 and see the quality yourself.
          </p>
        </div>
      </section>

      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Available data fields</h2>
          <p className="mt-2 text-muted-foreground text-sm">Fields available in the GST company database (subject to what is publicly available in GSTN filings).</p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {DATA_FIELDS.map((f) => (
              <div key={f} className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
                <Database className="h-4 w-4 text-primary" strokeWidth={2} /> {f}
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">Registry Feed does not guarantee any specific data field is populated for every row.</p>
        </div>
      </section>

      {/* Who uses GST data — updated per prompt */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Who Uses GST Data?</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { t: "Banks", d: "Current accounts, working capital, POS & merchant services for new businesses." },
              { t: "NBFCs", d: "Business loans, MSME credit, equipment finance for fresh GST registrations." },
              { t: "Marketing Agencies", d: "Digital marketing, websites, branding for new businesses." },
              { t: "Insurance Companies", d: "Business, shop, fire and employee insurance for new entities." },
              { t: "Sales Teams", d: "Any B2B product or service sold to newly GST-registered businesses." },
              { t: "CAs & Consultants", d: "GST return filing, reconciliation, audit and advisory services." },
            ].map((u) => (
              <div key={u.t} className="rounded-lg border border-border bg-card p-5">
                <div className="font-heading font-semibold">{u.t}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* State-wise GST Coverage */}
      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">State-wise GST Coverage</h2>
          <p className="mt-3 text-sm text-muted-foreground">New GST registrations from all 36 states and union territories, including:</p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-sm">
            {["Maharashtra","Delhi","Gujarat","Karnataka","Tamil Nadu","Uttar Pradesh","West Bengal","Telangana","Rajasthan","Haryana"].map((s) => (
              <div key={s} className="rounded-md border border-border bg-card px-3 py-2 text-center">{s}</div>
            ))}
          </div>
          <div className="mt-4 text-sm"><Link to="/data-coverage" className="text-primary hover:underline">View full pan-India coverage →</Link></div>
        </div>
      </section>

      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Coverage &amp; delivery</h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-sm">
            {COVERAGE_FACTS.map((c) => (
              <li key={c} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5" /> {c}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <WhatsAppButton message={WA_MESSAGES.gst} label="Buy GST Database" testid="gst-cta-buy" />
            <Link to="/pricing"><Button variant="outline">See Pricing</Button></Link>
            <Link to="/contact"><Button variant="ghost">Talk to Sales</Button></Link>
          </div>
        </div>
      </section>
    </PublicPageLayout>
  );
}
